import * as Dialog from '@radix-ui/react-dialog';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UserRole } from '../../../apiClients/workspace/types';
import { UserSelector } from '../../../components/shared';
import { Button } from '../../../components/shared/Button';
import { useLanguage } from '../../../hooks/shared';
import {
  useEventInstanceStore,
  useScheduleStore,
  useUserStore,
  useWorkspaceStore,
} from '../../../stores';
import { Participant, Training } from '../types/calendarTypes';
import ParticipantCard from './ParticipantCard';
import TemplateParticipantCard from './TemplateParticipantCard';

type TabMode = 'instance' | 'template';

interface ParticipantManagementModalProps {
  training: Training;
  isOpen: boolean;
  onClose: () => void;
}

const ParticipantManagementModal: React.FC<ParticipantManagementModalProps> =
  observer(({ training, isOpen, onClose }) => {
    const { t } = useLanguage();
    const eventInstanceStore = useEventInstanceStore();
    const scheduleStore = useScheduleStore();
    const userStore = useUserStore();
    const workspaceStore = useWorkspaceStore();

    // Check if training has a template
    const hasTemplate = !!training.templateId;

    // Default to 'template' mode if template exists, otherwise 'instance'
    const [activeTab, setActiveTab] = useState<TabMode>('instance');
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [isAdding, setIsAdding] = useState(false);

    // Get template from store
    const template = useMemo(() => {
      if (!training.templateId) return null;
      return scheduleStore.templates.find((t) => t.id === training.templateId);
    }, [training.templateId, scheduleStore.templates]);

    // Get template participants converted to Participant format
    const templateParticipants: Participant[] = useMemo(() => {
      if (!template) return [];
      return template.participants.map((tp) => {
        const user = userStore.users.get(tp.userId);
        return {
          id: tp.userId,
          name: user?.name || `User ${tp.userId}`,
          photoUrl: user?.photoUrl || undefined,
          status: 'Pending' as const,
        };
      });
    }, [template, userStore.users]);

    // Current participants based on active tab
    const currentParticipants =
      activeTab === 'template' ? templateParticipants : training.participants;

    // Load users when component mounts
    useEffect(() => {
      if (userStore.users.size === 0) {
        userStore.loadUsers();
      }
    }, [userStore]);

    // Reset state when modal opens/closes or tab changes
    useEffect(() => {
      if (isOpen) {
        setShowAddForm(false);
        setSelectedUserId('');
      }
    }, [isOpen, activeTab]);

    const handleClose = useCallback(() => {
      setShowAddForm(false);
      setSelectedUserId('');
      setActiveTab('instance');
      onClose();
    }, [onClose]);

    // Get available users for adding (not already participants)
    const availableUsersForAdd = useMemo(
      () =>
        Array.from(userStore.users.values())
          .filter(
            (user) =>
              !currentParticipants.some((participant) =>
                [participant.id, participant.replacementFor].includes(user.id)
              ) && user.roles.includes(UserRole.Participant)
          )
          .map((u) => ({
            id: u.id,
            name: u.name,
            photoUrl: u.photoUrl || undefined,
          })),
      [userStore.users, currentParticipants]
    );

    // ===============================
    // INSTANCE HANDLERS
    // ===============================

    const handleAddParticipant = async () => {
      if (!selectedUserId) return;

      setIsAdding(true);
      try {
        await eventInstanceStore.addParticipant(training.id, selectedUserId, {
          actor: UserRole.Admin,
        });
        setShowAddForm(false);
        setSelectedUserId('');
      } catch (error) {
        console.error('Failed to add participant:', error);
      } finally {
        setIsAdding(false);
      }
    };

    const handleConfirmParticipant = async (participantId: string) => {
      await eventInstanceStore.confirmParticipation(
        training.id,
        participantId,
        {
          actor: UserRole.Admin,
        }
      );
    };

    const handleCancelParticipant = async (
      participantId: string,
      withValidExcuse?: boolean
    ) => {
      await eventInstanceStore.cancelParticipation(training.id, participantId, {
        actor: UserRole.Admin,
        withValidExcuse,
      });
    };

    const handleReplaceParticipant = async (
      fromParticipantId: string,
      toUserId: string
    ) => {
      await eventInstanceStore.replaceParticipant(
        training.id,
        fromParticipantId,
        {
          replacingParticipantId: toUserId,
          actor: UserRole.Admin,
        }
      );
    };

    const handleDeleteParticipant = async (participantId: string) => {
      await eventInstanceStore.deleteParticipant(training.id, participantId);
    };

    // ===============================
    // TEMPLATE HANDLERS
    // ===============================

    const handleAddTemplateParticipant = async () => {
      if (!selectedUserId || !training.templateId) return;

      setIsAdding(true);
      try {
        await eventInstanceStore.addTemplateParticipant(
          training.templateId,
          selectedUserId
        );
        setShowAddForm(false);
        setSelectedUserId('');
      } catch (error) {
        console.error('Failed to add template participant:', error);
      } finally {
        setIsAdding(false);
      }
    };

    const handleReplaceTemplateParticipant = async (
      fromParticipantId: string,
      toUserId: string
    ) => {
      if (!training.templateId) return;
      await eventInstanceStore.replaceTemplateParticipant(
        training.templateId,
        fromParticipantId,
        toUserId
      );
    };

    const handleDeleteTemplateParticipant = async (participantId: string) => {
      if (!training.templateId) return;
      await eventInstanceStore.deleteTemplateParticipant(
        training.templateId,
        participantId
      );
    };

    if (!isOpen) return null;

    return (
      <Dialog.Root
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
        modal={true}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
          <Dialog.Content
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onInteractOutside={(e) => {
              // Prevent closing when clicking inside the modal
              e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              handleClose();
            }}
            onClick={(e) => {
              // Stop propagation to prevent clicks from reaching the training card
              e.stopPropagation();
            }}
            onPointerDown={(e) => {
              // Stop propagation on pointer down as well
              e.stopPropagation();
            }}
          >
            <div
              className="w-full max-w-md max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div
                className="bg-white rounded-lg shadow-lg flex flex-col max-h-full"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {t('calendar.participantActions')}
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                  >
                    ×
                  </button>
                </div>

                {/* Tabs - only show if training has template */}
                {hasTemplate && (
                  <div className="flex border-b border-gray-200">
                    <button
                      onClick={() => setActiveTab('instance')}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                        activeTab === 'instance'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {t('calendar.instanceTab')}
                    </button>
                    <button
                      onClick={() => setActiveTab('template')}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                        activeTab === 'template'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {t('calendar.templateTab')}
                    </button>
                  </div>
                )}

                {/* Content */}
                <div
                  className="flex-1 overflow-y-auto p-4 space-y-2"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  {/* Participants list */}
                  {activeTab === 'instance' ? (
                    // TODO refactor
                    <>
                      {training.participants.map((participant, index) => {
                        const availableUsersForReplace = Array.from(
                          userStore.users.values()
                        )
                          .filter(
                            (user) =>
                              !training.participants.some((p) =>
                                [p.id, p.replacementFor].includes(user.id)
                              ) &&
                              user.id !== participant.id &&
                              user.roles.includes(UserRole.Participant)
                          )
                          .map((u) => ({
                            id: u.id,
                            name: u.name,
                            photoUrl: u.photoUrl || undefined,
                          }));

                        return (
                          <ParticipantCard
                            key={participant.id}
                            participant={participant}
                            training={training}
                            onConfirm={handleConfirmParticipant}
                            onCancel={handleCancelParticipant}
                            onReplace={handleReplaceParticipant}
                            onDelete={handleDeleteParticipant}
                            availableUsersForReplace={availableUsersForReplace}
                            isFirst={index === 0}
                          />
                        );
                      })}
                    </>
                  ) : (
                    // Template participants
                    <>
                      {templateParticipants.map((participant, index) => {
                        const availableUsersForReplace = Array.from(
                          userStore.users.values()
                        )
                          .filter(
                            (user) =>
                              !templateParticipants.some(
                                (p) => p.id === user.id
                              ) &&
                              user.id !== participant.id &&
                              user.roles.includes(UserRole.Participant)
                          )
                          .map((u) => ({
                            id: u.id,
                            name: u.name,
                            photoUrl: u.photoUrl || undefined,
                          }));

                        return (
                          <TemplateParticipantCard
                            key={participant.id}
                            participant={participant}
                            onReplace={handleReplaceTemplateParticipant}
                            onDelete={handleDeleteTemplateParticipant}
                            availableUsersForReplace={availableUsersForReplace}
                            isFirst={index === 0}
                          />
                        );
                      })}
                    </>
                  )}

                  {/* Add participant placeholder or form */}
                  {(!workspaceStore.settings?.maxParticipantsPerEvent ||
                    workspaceStore.settings.maxParticipantsPerEvent >
                      currentParticipants.length) && (
                    <>
                      {!showAddForm ? (
                        <button
                          onClick={() => setShowAddForm(true)}
                          className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600"
                        >
                          <span className="text-xl">+</span>
                          <span className="font-medium">
                            {t('calendar.addParticipant')}
                          </span>
                        </button>
                      ) : (
                        <div className="p-4 border-2 border-blue-400 rounded-lg bg-blue-50 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('calendar.selectParticipant')}
                            </label>
                            <UserSelector
                              users={availableUsersForAdd}
                              value={selectedUserId}
                              onChange={setSelectedUserId}
                              placeholder={t('calendar.selectParticipant')}
                              disabled={isAdding}
                              dropdownDirection="up"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowAddForm(false);
                                setSelectedUserId('');
                              }}
                              disabled={isAdding}
                              className="flex-1"
                            >
                              {t('common.cancel')}
                            </Button>
                            <Button
                              size="sm"
                              onClick={
                                activeTab === 'template'
                                  ? handleAddTemplateParticipant
                                  : handleAddParticipant
                              }
                              disabled={!selectedUserId || isAdding}
                              className="flex-1"
                            >
                              {isAdding
                                ? t('common.saving')
                                : t('common.confirm')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  });

export default ParticipantManagementModal;
